import { AssistantWsConnection, COACH_ASK_ERROR_LABELS, CoachCallStatePayload, CoachFundingBasketPayload, CoachSmartCatalogPayload, CoachSmartCatalogVerbatimsPayload, CoachSuggestionPayload, mergeCoachCallState, mergeSmartCatalogVerbatims, SmartCatalogOptions, SmartCatalogOverridePayload, walterAssistantClient } from "@/services/walter-assistant";
import { FundingBasketItem, FundingBasketOverrideMode } from "@/types/content/funding_basket";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useUser } from "./user";

const MAX_DISPLAYED_SUGGESTIONS = 20;
const REFETCH_DEBOUNCE_MS = 300;
const MANUAL_SUGGESTION_TTL_SECONDS = 60;
/**
 * Durée (ms) avant que le watchdog ne sorte automatiquement de l'état
 * `isSmartCatalogPending` quand aucun payload n'est arrivé. Couvre les
 * cas où le backend skip silencieusement la génération initiale (origine
 * non résolue, lead santé non qualifié, feature off…).
 */
const SMART_CATALOG_PENDING_TIMEOUT_MS = 12_000;

function createManualStreamingPlaceholder(askId: string, callId: string): CoachSuggestionPayload {
    const nowMs = Date.now();
    return {
        type: "coach.suggestion",
        call_id: callId,
        group_id: "",
        timestamp_ms: nowMs,
        suggestion_id: askId,
        suggestion_code: null,
        source: "manual",
        priority: "medium",
        suggested_answer: "",
        rationale: null,
        ttl_seconds: MANUAL_SUGGESTION_TTL_SECONDS,
        expires_at: new Date(nowMs + MANUAL_SUGGESTION_TTL_SECONDS * 1000).toISOString(),
        evidence: null,
        detected_signal: null,
        action_type: null,
        example_handoff_phrase: null,
        isStreaming: true
    };
}

/**
 * État unifié exposé aux consumers (panel, utility-bar). Quatre valeurs
 * orientées UX, dérivées de quatre axes :
 *
 * - `not_connected`   : pas de pont actif avec l'app desktop. Soit la WS du
 *                       CRM n'est pas (encore) ouverte, soit aucune
 *                       `DesktopControlConsumer` desktop n'est enregistrée
 *                       pour ce user. Côté UX : "lance ton app desktop".
 * - `open_idle`       : WS CRM ouverte + desktop en ligne + aucun appel
 *                       actif. Coach prêt, en attente.
 * - `open_in_call`    : WS CRM ouverte + desktop en ligne + appel en cours.
 * - `open_post_call`  : un appel vient de se terminer ; on garde le snapshot
 *                       (suggestions + roadmap + objections) jusqu'au
 *                       prochain `call.start` pour permettre au commercial
 *                       de relire le contexte. Cet état "sticky" survit à
 *                       une déconnexion WS ou à une perte du desktop —
 *                       seul un nouvel appel ou une réinitialisation
 *                       explicite (REST refetch avec `has_active_call: true`)
 *                       le réinitialise.
 *
 * On ne distingue plus volontairement "WS CRM en handshake" et "desktop
 * absent" : tant que tout n'est pas en place pour faire du coaching, on
 * affiche un seul et même message orienté action utilisateur.
 *
 * ## Machine à états (textuelle)
 *
 * États : `not_connected`, `open_idle`, `open_in_call`, `open_post_call`.
 *
 * Variables internes :
 *  - `wsState` : "closed" | "connecting" | "open" — état du WebSocket CRM.
 *  - `desktopConnected` : bool — présence de l'app desktop côté backend.
 *  - `activeCall` : {id} | null — appel actuellement en cours.
 *  - `recapAvailable` : bool — un appel s'est terminé sur cette session
 *    et on n'en a pas encore commencé un nouveau ; force `open_post_call`.
 *  - `lastKnownCallId` : string | null — sert à distinguer "fresh session"
 *    de "session qui a vu passer un appel" pour la résolution REST.
 *
 * Transitions :
 *  - `call.start` : reset propre. activeCall = {id}, suggestions = [],
 *    callState = null, recapAvailable = false, lastKnownCallId = id.
 *  - `call.end` : recapAvailable = true. **NE PAS** vider suggestions ni
 *    callState — c'est ce snapshot figé qui alimente le recap.
 *  - REST `has_active_call: true` : activeCall = {id}, recapAvailable = false,
 *    lastKnownCallId = id, suggestions remplacés par ceux du payload.
 *  - REST `has_active_call: false` :
 *    - si `lastKnownCallId` est set (on a vu un appel sur cette session) →
 *      recapAvailable = true (on entre / reste en post-call), on ne vide
 *      PAS le snapshot local (les données REST sont périmées une fois
 *      l'appel terminé).
 *    - sinon (fresh boot, jamais vu d'appel) → recapAvailable = false, on
 *      remplace suggestions par celles du payload (probablement vides).
 *  - `desktop.presence false` : on coupe activeCall localement mais **on
 *    ne touche pas à recapAvailable** — si on était déjà en post-call,
 *    on y reste.
 *  - WS `close` : pareil. Le recap est préservé jusqu'au prochain
 *    `call.start` ou refetch REST avec un appel actif.
 *
 * Dérivation finale :
 *  - `recapAvailable` → `open_post_call` (priorité maximale).
 *  - sinon, si `wsState != "open"` ou `!desktopConnected` → `not_connected`.
 *  - sinon, si `activeCall` → `open_in_call`.
 *  - sinon → `open_idle`.
 */
export type CoachState = "not_connected" | "open_idle" | "open_in_call" | "open_post_call";

type WsState = "closed" | "connecting" | "open";

type ActiveCall = { id: string } | null;

type WalterCoachContextType = {
    coachState: CoachState;
    sessionReplaced: boolean;
    suggestions: CoachSuggestionPayload[];
    /**
     * Dernier snapshot stratégique reçu du Conductor (Layer 2). En
     * `open_post_call`, ce snapshot est figé sur le dernier `coach.call_state`
     * reçu avant `call.end` — il sert au recap (roadmap des phases,
     * milestones validés, objections détectées). Reset à `null`
     * sur `call.start` pour repartir propre.
     */
    callState: CoachCallStatePayload | null;
    serverTime: string | null;
    isLoading: boolean;
    errorText: string | null;
    resumeSession: () => void;
    reloadInitialSuggestions: () => Promise<void>;
    /** Déclenche un flux `coach.ask` ("Qu'est-ce que je dois dire ?"). */
    askCoach: () => void;
    /** True pendant qu'un verbatim est en cours de génération. */
    isAskingCoach: boolean;
    /** Kill-switch backend (`coach.ask.error code=disabled`). */
    askCoachDisabled: boolean;
    /**
     * Dernier payload `coach.smart_catalog` connu (alimenté par le WS
     * + hydraté via `getCurrentCoachSuggestions` qui embarque le
     * payload en bonus). Reset à `null` à chaque `call.start`. Préservé
     * en post-call comme `suggestions` / `callState`.
     */
    smartCatalog: CoachSmartCatalogPayload | null;
    /**
     * True quand une régénération catalogue est en vol (Phase A) :
     * - juste après un `call.start` (en attente du premier push), OU
     * - après un clic sur "Mettre à jour le catalogue" (jusqu'au
     *   prochain `coach.smart_catalog` ou erreur HTTP).
     *
     * Sert à désactiver le bouton et à afficher un skeleton quand on
     * n'a aucun payload précédent à montrer.
     */
    isSmartCatalogPending: boolean;
    /**
     * True quand la Phase B (génération LLM des verbatims) est en cours.
     * Sert à désactiver le bouton "Mettre à jour" jusqu'à la fin du cooldown.
     */
    isVerbatimPending: boolean;
    /**
     * Déclenche un POST `/refresh/` (anti-rebond côté backend). Le
     * résultat arrive par WS, pas dans la réponse HTTP — on bascule
     * juste `isSmartCatalogPending` pour le visuel.
     */
    refreshSmartCatalog: () => Promise<void>;
    /**
     * Picklists des trois axes d'override (profession, statut
     * professionnel, financeur) + mapping statut→financeurs. Récupérées
     * une fois au boot (cache backend). `null` tant que non chargées ou
     * si le CRM est indisponible — l'UI masque alors les filtres et
     * dégrade sur la bannière "profession détectée".
     */
    smartCatalogOptions: SmartCatalogOptions | null;
    /**
     * Override agent d'un ou plusieurs axes (sticky, prioritaire sur la
     * détection). `""` sur un axe ⇒ reset (retour à la détection). Comme
     * `refreshSmartCatalog`, le résultat arrive par WS : on bascule
     * `isSmartCatalogPending` le temps de la régénération.
     */
    overrideSmartCatalog: (payload: SmartCatalogOverridePayload) => Promise<void>;
    /**
     * Demande on-demand la génération de verbatims pour les theme_codes fournis.
     * Filtre automatiquement les codes qui ont déjà un verbatim `status: "ready"`.
     * Les verbatims arrivent par WS `coach.smart_catalog_verbatims` — aucune
     * mise à jour d'état locale lors de l'appel.
     */
    requestVerbatims: (themeCodes: string[]) => Promise<void>;
    /**
     * Dernier payload `coach.funding_basket` (tableau financeur : une
     * ligne par financeur du playbook + jauges + thèmes phares + panier).
     * `null` quand le playbook n'expose aucun financeur (table masquée)
     * ou hors appel. Reset sur `call.start`, préservé en post-call.
     */
    fundingBasket: CoachFundingBasketPayload | null;
    /** True pendant une mutation panier / override (boutons disabled). */
    isFundingBasketPending: boolean;
    /** Ajoute une formation au panier financeur (persistant sur le call group). */
    addBasketItem: (input: FundingBasketItem) => Promise<void>;
    /** Retire une formation du panier financeur. */
    removeBasketItem: (input: { product_code: string; funding_code: string }) => Promise<void>;
    /** Définit/réinitialise l'override de budget d'un financeur (ex. CPF). */
    setEnvelopeOverride: (input: { funding_code: string; mode?: FundingBasketOverrideMode; amount?: number; reset?: boolean }) => Promise<void>;
    /**
     * Vide immédiatement la liste des suggestions côté client. À appeler
     * après un `switchPlaybook` réussi pour éviter d'afficher des conseils
     * obsolètes du précédent playbook avant que les nouveaux n'arrivent par WS.
     */
    clearSuggestions: () => void;
};

const WalterCoachContext = React.createContext<WalterCoachContextType>(null!);

export function WalterCoachProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const hasCoachAccess = Boolean(user?.walter_assistant_coach);

    const [wsState, setWsState] = useState<WsState>("closed");
    const [activeCall, setActiveCall] = useState<ActiveCall>(null);
    const [desktopConnected, setDesktopConnected] = useState(false);
    const [sessionReplaced, setSessionReplaced] = useState(false);
    const [suggestions, setSuggestions] = useState<CoachSuggestionPayload[]>([]);
    const [callState, setCallState] = useState<CoachCallStatePayload | null>(null);
    const [serverTime, setServerTime] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [reconnectKey, setReconnectKey] = useState(0);
    const [isAskingCoach, setIsAskingCoach] = useState(false);
    const [askCoachDisabled, setAskCoachDisabled] = useState(false);
    const [smartCatalog, setSmartCatalog] = useState<CoachSmartCatalogPayload | null>(null);
    const [isSmartCatalogPending, setIsSmartCatalogPending] = useState(false);
    const [isVerbatimPending, setIsVerbatimPending] = useState(false);
    const [smartCatalogOptions, setSmartCatalogOptions] = useState<SmartCatalogOptions | null>(null);
    /**
     * Dernier payload `coach.funding_basket` (tableau financeur). Même
     * cycle de vie que `smartCatalog` : poussé par WS, hydraté au boot,
     * reset à `null` sur `call.start`, préservé en post-call. `null` quand
     * le playbook n'expose aucun financeur (table masquée).
     */
    const [fundingBasket, setFundingBasket] = useState<CoachFundingBasketPayload | null>(null);
    /** True pendant une mutation panier / override en attente du recalcul. */
    const [isFundingBasketPending, setIsFundingBasketPending] = useState(false);
    const connectionRef = useRef<AssistantWsConnection | null>(null);
    const askIdRef = useRef<string | null>(null);
    const askMetaRef = useRef<{ callId: string; groupId: string; timestampMs: number } | null>(null);
    const activeCallRef = useRef<ActiveCall>(null);
    /**
     * Drapeau "un appel vient de se terminer, garder le contexte" — voir
     * la docstring de `CoachState` plus haut pour les transitions exactes.
     */
    const [recapAvailable, setRecapAvailable] = useState(false);
    /**
     * Mémoire de session : a-t-on déjà vu un appel sur cette page ? Sert
     * uniquement à la résolution REST (savoir si `has_active_call: false`
     * doit nous mettre en `open_post_call` ou `open_idle`). Non
     * reset à la déconnexion WS — c'est une mémoire UX.
     */
    const lastKnownCallIdRef = useRef<string | null>(null);

    useEffect(() => {
        activeCallRef.current = activeCall;
    }, [activeCall]);

    const upsertSuggestion = useCallback((suggestion: CoachSuggestionPayload) => {
        setSuggestions(previous => {
            const without = previous.filter(item => item.suggestion_id !== suggestion.suggestion_id);
            const next = [suggestion, ...without];
            return next.slice(0, MAX_DISPLAYED_SUGGESTIONS);
        });
    }, []);

    /**
     * Fetch REST de réconciliation : SEULE source de vérité pour `activeCall`
     * et seed pour `desktopConnected`. Les events WS `call.start`/`call.end`
     * et `desktop.presence` ne sont que des hints qui déclenchent ce refetch
     * (et positionnent immédiatement `desktopConnected` pour la réactivité).
     * Un event manqué n'aboutit jamais à une UI bloquée puisqu'on
     * resynchronise depuis la DB / le cache à la prochaine occasion.
     */
    const reloadInitialSuggestions = useCallback(async () => {
        if (!hasCoachAccess) return;
        setIsLoading(true);
        setErrorText(null);
        try {
            const payload = await walterAssistantClient.getCurrentCoachSuggestions();
            setServerTime(payload.server_time);
            setDesktopConnected(Boolean(payload.has_active_desktop));

            if (payload.has_active_call && payload.call_id) {
                /*
                 * Un appel actif sort toujours du recap : on remplace les
                 * suggestions par celles du payload (autoritatif côté
                 * backend pour cet appel), on note l'ID et on remet
                 * `recapAvailable` à false.
                 */
                setActiveCall({ id: payload.call_id });
                lastKnownCallIdRef.current = payload.call_id;
                setRecapAvailable(false);
                setSuggestions(payload.suggestions);
                if (payload.call_state) {
                    setCallState(previous => mergeCoachCallState(previous, payload.call_state!));
                }
                /*
                 * Le payload REST embarque le dernier `coach.smart_catalog`
                 * connu pour cet appel actif. Quand il est présent on
                 * l'écrase (autoritatif) et on stoppe le pending. Quand il
                 * est `null` on bascule en pending MAIS on NE VIDE PAS un
                 * catalogue déjà reçu via WS : un refetch debouncé (déclenché
                 * par `desktop.presence` / `call.end`) peut courir pendant
                 * qu'une régénération est en vol côté backend et renvoyer un
                 * `null` transitoire — l'écraser ferait flasher le skeleton
                 * par-dessus un catalogue live valide. `call.start` reste le
                 * seul point qui remet le catalogue à `null` pour un nouvel
                 * appel.
                 */
                if (payload.smart_catalog) {
                    setSmartCatalog(payload.smart_catalog);
                    setIsSmartCatalogPending(false);
                } else {
                    setIsSmartCatalogPending(true);
                }
                /*
                 * Le tableau financeur n'est pas embarqué dans le payload
                 * suggestions (endpoint dédié, recalcul CRM coûteux). On
                 * l'hydrate en best-effort : un `null` (playbook sans
                 * financeur, ou recalcul indisponible) ne doit pas écraser
                 * un payload live déjà reçu par WS — même garde que le
                 * Smart Catalog. `call.start` reste le seul reset.
                 */
                void (async () => {
                    try {
                        const fb = await walterAssistantClient.fetchCurrentFundingBasket();
                        if (fb.funding_basket) setFundingBasket(fb.funding_basket);
                    } catch {
                        // Hydratation best-effort : on garde le dernier WS connu.
                    }
                })();
            } else {
                setActiveCall(null);
                if (lastKnownCallIdRef.current) {
                    /*
                     * On a déjà vu passer un appel sur cette session : on
                     * entre (ou reste) en recap. Surtout NE PAS écraser
                     * `suggestions` / `callState` avec le payload REST :
                     * le serveur ne renvoie plus rien d'utile pour un
                     * appel terminé, et notre snapshot local est la
                     * source de vérité du recap.
                     */
                    setRecapAvailable(true);
                } else {
                    /*
                     * Fresh boot, jamais d'appel sur cette session : on
                     * affiche les suggestions du payload (probablement
                     * vides) et on reste en idle.
                     */
                    setRecapAvailable(false);
                    setSuggestions(payload.suggestions);
                    /*
                     * Pas d'appel actif ⇒ pas de catalogue à afficher.
                     * On nettoie sans bloquer le panel en pending pour
                     * que la tab affiche l'empty state pré-appel.
                     */
                    setSmartCatalog(null);
                    setIsSmartCatalogPending(false);
                    setFundingBasket(null);
                }
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Impossible de récupérer les conseils coach.";
            setErrorText(message);
        } finally {
            setIsLoading(false);
        }
    }, [hasCoachAccess]);

    const reloadInitialSuggestionsRef = useRef(reloadInitialSuggestions);
    useEffect(() => {
        reloadInitialSuggestionsRef.current = reloadInitialSuggestions;
    }, [reloadInitialSuggestions]);

    const refetchTimerRef = useRef<number | null>(null);
    const scheduleRefetch = useCallback(() => {
        if (refetchTimerRef.current !== null) {
            window.clearTimeout(refetchTimerRef.current);
        }
        refetchTimerRef.current = window.setTimeout(() => {
            refetchTimerRef.current = null;
            void reloadInitialSuggestionsRef.current();
        }, REFETCH_DEBOUNCE_MS);
    }, []);

    useEffect(() => {
        return () => {
            if (refetchTimerRef.current !== null) {
                window.clearTimeout(refetchTimerRef.current);
                refetchTimerRef.current = null;
            }
        };
    }, []);

    /*
     * Picklists des filtres d'override : fetch unique au boot dès qu'on a
     * l'accès coach. Les listes bougent rarement (cache backend), donc
     * pas de refetch sur changement d'appel. Échec silencieux : si le
     * CRM est indisponible (`502 options_unavailable`) ou réseau KO, on
     * garde `null` et le panel masque les filtres (dégradation propre).
     */
    useEffect(() => {
        if (!hasCoachAccess) return;
        let cancelled = false;
        void (async () => {
            try {
                const options = await walterAssistantClient.fetchSmartCatalogOptions();
                if (!cancelled) setSmartCatalogOptions(options);
            } catch {
                // Options indisponibles : on dégrade en masquant les filtres.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hasCoachAccess]);

    useEffect(() => {
        if (!hasCoachAccess || sessionReplaced) {
            setWsState("closed");
            setActiveCall(null);
            setDesktopConnected(false);
            return;
        }

        const connection: AssistantWsConnection = walterAssistantClient.openCoachSocket({
            onConnectionChange: state => {
                setWsState(state);
                if (state === "open") {
                    void reloadInitialSuggestionsRef.current();
                } else if (state === "closed") {
                    /*
                     * Ne pas reset `desktopConnected` ici : le serveur peut
                     * notifier `desktop.presence` qu'on récupérera dans le
                     * seed à la reconnexion. Garder la dernière valeur
                     * connue évite un flash "Not connected" pendant un blip
                     * réseau de la WS CRM alors que le desktop est toujours
                     * actif côté backend.
                     *
                     * On coupe `activeCall` localement (l'appel ne peut
                     * pas continuer sans WS) MAIS on préserve
                     * `recapAvailable` : si on était déjà en post-call,
                     * le recap doit rester visible le temps de la
                     * reconnexion.
                     */
                    setActiveCall(null);
                }
            },
            onCallStart: event => {
                /*
                 * Reset complet : nouveau call → on jette le recap
                 * éventuel du précédent appel, on repart sur des
                 * suggestions et un callState vides, et on mémorise
                 * l'ID pour la résolution REST ultérieure.
                 */
                setSuggestions([]);
                setCallState(null);
                setRecapAvailable(false);
                lastKnownCallIdRef.current = event.call_id;
                /*
                 * Smart Catalog : on jette aussi le catalogue du
                 * précédent appel (sinon le commercial verrait pendant
                 * les premières secondes du nouveau call le pool du
                 * prospect précédent) et on bascule en pending — la
                 * première génération arrive automatiquement côté
                 * backend (`enqueue_initial_regeneration`).
                 */
                setSmartCatalog(null);
                setIsSmartCatalogPending(true);
                setIsVerbatimPending(false);
                /*
                 * Tableau financeur : on jette le panier/affichage du
                 * précédent appel. Le backend recalcule et pousse un
                 * nouveau `coach.funding_basket` dans la foulée de la
                 * première régénération Smart Catalog du nouvel appel.
                 */
                setFundingBasket(null);
                setIsFundingBasketPending(false);
                scheduleRefetch();
            },
            onCallEnd: () => {
                /*
                 * Fin d'appel : on entre en mode recap. On NE VIDE PAS
                 * `suggestions` ni `callState` — ce sont précisément ces
                 * snapshots figés qui alimentent le recap dans le panel.
                 * Ils seront remplacés au prochain `call.start`.
                 */
                setRecapAvailable(true);
                scheduleRefetch();
            },
            onDesktopPresence: online => {
                /*
                 * Mise à jour optimiste pour la réactivité : on bascule le
                 * badge tout de suite, le refetch debouncé qui suit ne fait
                 * que confirmer (et corriger l'éventuelle divergence avec
                 * la DB côté `activeCall`).
                 */
                setDesktopConnected(online);
                if (!online) {
                    /*
                     * Si le desktop disparaît, l'appel n'a plus de capture
                     * audio derrière : on coupe activeCall localement
                     * sans attendre le refetch. Le backend a normalement
                     * déjà broadcasté `call.end` (logout → DELETE), mais
                     * s'il a manqué pour une raison X, on n'aura pas
                     * l'UI bloquée en "En cours" avec un desktop offline.
                     *
                     * On NE TOUCHE PAS à `recapAvailable` : si on était
                     * déjà en post-call, le recap reste visible (c'est
                     * exactement le cas où le commercial veut relire son
                     * appel après avoir éteint le desktop).
                     */
                    setActiveCall(null);
                }
                scheduleRefetch();
            },
            onCallState: state => {
                /*
                 * Le dernier snapshot du Conductor (L2) gagne toujours. Il n'y
                 * a qu'un seul état pilote actif : phase + risque + critères
                 * + roadmap + objections sont mis à jour en bloc à chaque
                 * tick (≈ toutes les 15s ou sur strong event), garantissant
                 * une vue cohérente.
                 */
                setCallState(previous => mergeCoachCallState(previous, state));
                setServerTime(new Date().toISOString());
            },
            onSmartCatalog: payload => {
                /*
                 * Phase A terminée : catalogue rapide avec verbatims en
                 * placeholder. On merge avec le previous pour préserver
                 * les verbatims déjà `ready` — évite le retour au skeleton
                 * quand un signal_change de fin d'appel relance Phase A
                 * alors que Phase B a déjà tourné.
                 *
                 * `isVerbatimPending` est dérivé du résultat réel : `true`
                 * seulement si au moins un thème est encore `pending` après
                 * le merge (et non systématiquement `true`).
                 */
                setSmartCatalog(prev => {
                    const merged = mergeSmartCatalogVerbatims(prev, payload);
                    const anyPending = merged.themes.some(t => t.verbatim?.verbatim_status !== "ready");
                    setIsVerbatimPending(anyPending);
                    return merged;
                });
                setIsSmartCatalogPending(false);
                setServerTime(new Date().toISOString());
            },
            onSmartCatalogVerbatims: (payload: CoachSmartCatalogVerbatimsPayload) => {
                /*
                 * Phase B terminée : on merge les verbatims dans le
                 * catalogue déjà affiché par theme_code. Le payload
                 * catalogue n'est PAS remplacé — on préserve l'ordre et
                 * les produits, on injecte seulement les champs verbatim.
                 */
                setSmartCatalog(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        themes: prev.themes.map(theme => {
                            const verbatim = payload.verbatims[theme.theme_code];
                            if (!verbatim) return theme;
                            return { ...theme, verbatim };
                        }),
                    };
                });
                setIsVerbatimPending(false);
                setServerTime(new Date().toISOString());
            },
            onFundingBasket: payload => {
                /*
                 * Tableau financeur recalculé côté backend (régénération
                 * Smart Catalog, mutation panier ou override d'enveloppe).
                 * Le dernier payload est autoritatif : on remplace en bloc
                 * et on retombe le pending de mutation.
                 */
                setFundingBasket(payload);
                setIsFundingBasketPending(false);
                setServerTime(new Date().toISOString());
            },
            onSuggestion: suggestion => {
                setServerTime(new Date().toISOString());
                setSuggestions(previous => {
                    /*
                     * Dédup défensive par `suggestion_id` (clé stable
                     * backend) : protège contre un éventuel re-emit du
                     * même `coach.suggestion` (retry, double subscribe).
                     * La dernière instance gagne et est placée en tête.
                     */
                    const without = previous.filter(item => item.suggestion_id !== suggestion.suggestion_id);
                    const next = [suggestion, ...without];
                    return next.slice(0, MAX_DISPLAYED_SUGGESTIONS);
                });
                setErrorText(null);
            },
            onSessionReplaced: () => {
                setSessionReplaced(true);
            },
            onAuthFailure: () => {
                setErrorText("Connexion au coach impossible : authentification refusée.");
            },
            onAskStart: event => {
                if (event.ask_id !== askIdRef.current) return;
                askMetaRef.current = {
                    callId: event.call_id,
                    groupId: event.group_id,
                    timestampMs: event.timestamp_ms
                };
                setIsAskingCoach(true);
                upsertSuggestion({
                    ...createManualStreamingPlaceholder(event.ask_id, event.call_id),
                    group_id: event.group_id,
                    timestamp_ms: event.timestamp_ms
                });
            },
            onAskDelta: event => {
                if (event.ask_id !== askIdRef.current) return;
                setSuggestions(previous =>
                    previous.map(item =>
                        item.suggestion_id === event.ask_id
                            ? { ...item, suggested_answer: item.suggested_answer + event.text, isStreaming: true }
                            : item
                    )
                );
            },
            onAskDone: event => {
                if (event.ask_id !== askIdRef.current) return;
                setIsAskingCoach(false);
                const meta = askMetaRef.current;
                const finalId = event.suggestion_id || event.ask_id;
                const timestampMs = meta?.timestampMs ?? Date.now();
                setSuggestions(previous => {
                    const without = previous.filter(item => item.suggestion_id !== event.ask_id && item.suggestion_id !== finalId);
                    const final: CoachSuggestionPayload = {
                        type: "coach.suggestion",
                        call_id: meta?.callId ?? "",
                        group_id: meta?.groupId ?? "",
                        timestamp_ms: timestampMs,
                        suggestion_id: finalId,
                        suggestion_code: null,
                        source: "manual",
                        priority: "medium",
                        suggested_answer: event.full_text,
                        rationale: null,
                        ttl_seconds: MANUAL_SUGGESTION_TTL_SECONDS,
                        expires_at: new Date(timestampMs + MANUAL_SUGGESTION_TTL_SECONDS * 1000).toISOString(),
                        evidence: null,
                        detected_signal: null,
                        action_type: null,
                        example_handoff_phrase: null
                    };
                    return [final, ...without].slice(0, MAX_DISPLAYED_SUGGESTIONS);
                });
                setServerTime(new Date().toISOString());
            },
            onAskError: event => {
                if (event.code === "cancelled" && event.ask_id !== askIdRef.current) return;
                if (event.ask_id !== askIdRef.current) return;
                setIsAskingCoach(false);
                setSuggestions(previous => previous.filter(item => item.suggestion_id !== event.ask_id));
                if (event.code === "disabled") {
                    setAskCoachDisabled(true);
                }
                const label = COACH_ASK_ERROR_LABELS[event.code];
                if (label) {
                    toast.error(label);
                }
            }
        });

        connectionRef.current = connection;

        return () => {
            connectionRef.current = null;
            connection.close();
        };
    }, [hasCoachAccess, reconnectKey, scheduleRefetch, sessionReplaced, upsertSuggestion]);

    /*
     * Watchdog sur le pending initial du Smart Catalog. Si `isSmartCatalogPending`
     * est `true` mais qu'aucun payload n'est arrivé après
     * `SMART_CATALOG_PENDING_TIMEOUT_MS`, on sort du skeleton pour ne pas
     * bloquer l'UI indéfiniment. Typiquement : backend qui skip la génération
     * initiale (lead santé non qualifié, origine inconnue, feature off) sans
     * émettre de WS — le frontend n'a aucun autre moyen de le savoir.
     *
     * Le timer est armé uniquement quand pending + pas de payload (cas à risque).
     * Il est annulé immédiatement si un payload arrive ou si pending retombe à
     * `false` (bonne résolution) ou à chaque nouveau `call.start` (reset).
     */
    useEffect(() => {
        if (!isSmartCatalogPending || smartCatalog !== null) return;
        const timerId = window.setTimeout(() => {
            setIsSmartCatalogPending(prev => {
                if (prev) return false;
                return prev;
            });
        }, SMART_CATALOG_PENDING_TIMEOUT_MS);
        return () => window.clearTimeout(timerId);
    }, [isSmartCatalogPending, smartCatalog]);

    const resumeSession = useCallback(() => {
        setSessionReplaced(false);
        setReconnectKey(key => key + 1);
        /*
         * Reprise de session = on repart d'un état propre (le recap d'un
         * précédent appel sur l'autre onglet n'a plus de sens ici).
         */
        setRecapAvailable(false);
        lastKnownCallIdRef.current = null;
        setSuggestions([]);
        setCallState(null);
        setSmartCatalog(null);
        setIsSmartCatalogPending(false);
        setFundingBasket(null);
        setIsFundingBasketPending(false);
        void reloadInitialSuggestions();
    }, [reloadInitialSuggestions]);

    const refreshSmartCatalog = useCallback(async () => {
        /*
         * Bouton "Mettre à jour le catalogue". On bascule le pending
         * tout de suite pour le visuel (bouton disabled + spinner) et
         * on délègue au backend l'enqueue de la régénération. Le
         * résultat arrive par WS — on retombe à `false` à la
         * réception du prochain `coach.smart_catalog`.
         *
         * En cas d'erreur HTTP (réseau, 404 sans appel actif, etc.)
         * on retombe à `false` immédiatement pour ne pas laisser le
         * bouton bloqué. On log silencieusement : pas de toast — le
         * bouton lui-même affichera "Mise à jour échouée — réessayer"
         * sur le prochain payload `status === "failed"`. Pour un échec
         * franc d'enqueue (réseau), on s'autorise un toast court.
         */
        setIsSmartCatalogPending(true);
        try {
            await walterAssistantClient.refreshSmartCatalog();
        } catch (error) {
            setIsSmartCatalogPending(false);
            const message = error instanceof Error ? error.message : "Impossible de relancer la mise à jour du catalogue.";
            toast.error(message);
        }
    }, []);

    const overrideSmartCatalog = useCallback(async (payload: SmartCatalogOverridePayload) => {
        /*
         * Override agent (set ou reset d'un axe). Même contrat que
         * `refreshSmartCatalog` : on passe pending pour le visuel
         * (sélecteurs disabled + skeleton), et le nouveau classement
         * arrive par WS `coach.smart_catalog` qui retombe le pending.
         * En cas d'échec HTTP (404 sans appel actif, 400 code invalide,
         * réseau) on retombe à `false` et on signale via toast pour ne
         * pas laisser les contrôles bloqués.
         */
        setIsSmartCatalogPending(true);
        try {
            await walterAssistantClient.overrideSmartCatalog(payload);
        } catch (error) {
            setIsSmartCatalogPending(false);
            const message = error instanceof Error ? error.message : "Impossible d'appliquer le filtre.";
            toast.error(message);
        }
    }, []);

    const requestVerbatims = useCallback(async (themeCodes: string[]) => {
        /*
         * On-demand : ne déclenche la génération que pour les codes qui n'ont
         * pas encore de verbatim ready, afin d'éviter un appel LLM inutile
         * si Phase B eager a déjà tout couvert.
         */
        const currentThemes = smartCatalog?.themes ?? [];
        const pendingCodes = themeCodes.filter(code => {
            const theme = currentThemes.find(t => t.theme_code === code);
            return !theme || theme.verbatim?.verbatim_status !== "ready";
        });
        if (pendingCodes.length === 0) return;
        try {
            await walterAssistantClient.requestVerbatims(pendingCodes);
        } catch {
            // Silent fail: the theme cards will keep showing their skeleton
            // until the next successful Phase B run or a manual refresh.
        }
    }, [smartCatalog]);

    const addBasketItem = useCallback(async (input: FundingBasketItem) => {
        /*
         * Ajout au panier. Le backend mute le `FundingBasketState`
         * persistant, recalcule la vue (jauges + ré-ordonnancement des
         * thèmes) et la pousse par WS — mais la réponse HTTP embarque
         * déjà le payload recalculé : on l'applique en optimiste pour
         * éviter d'attendre l'aller-retour WS. On bascule pending pour
         * le visuel (boutons disabled le temps du recalcul).
         */
        setIsFundingBasketPending(true);
        try {
            const res = await walterAssistantClient.updateFundingBasketItem({ action: "add", ...input });
            if (res.funding_basket) setFundingBasket(res.funding_basket);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Impossible d'ajouter la formation au panier.";
            toast.error(message);
        } finally {
            setIsFundingBasketPending(false);
        }
    }, []);

    const removeBasketItem = useCallback(async (input: { product_code: string; funding_code: string }) => {
        setIsFundingBasketPending(true);
        try {
            const res = await walterAssistantClient.updateFundingBasketItem({ action: "remove", ...input });
            if (res.funding_basket) setFundingBasket(res.funding_basket);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Impossible de retirer la formation du panier.";
            toast.error(message);
        } finally {
            setIsFundingBasketPending(false);
        }
    }, []);

    const setEnvelopeOverride = useCallback(
        async (input: { funding_code: string; mode?: FundingBasketOverrideMode; amount?: number; reset?: boolean }) => {
            /*
             * Ajustement (ou reset) du budget d'un financeur — typiquement
             * le budget CPF par défaut corrigé par le commercial. Même
             * contrat optimiste que les mutations panier.
             */
            setIsFundingBasketPending(true);
            try {
                const res = await walterAssistantClient.updateFundingBasketEnvelopeOverride(input);
                if (res.funding_basket) setFundingBasket(res.funding_basket);
            } catch (error) {
                const message = error instanceof Error ? error.message : "Impossible de mettre à jour l'enveloppe.";
                toast.error(message);
            } finally {
                setIsFundingBasketPending(false);
            }
        },
        []
    );

    const askCoach = useCallback(() => {
        const connection = connectionRef.current;
        if (!connection || askCoachDisabled) return;
        const callId = activeCallRef.current?.id ?? lastKnownCallIdRef.current ?? undefined;
        const askId = connection.ask(callId);
        askIdRef.current = askId;
        setIsAskingCoach(true);
        upsertSuggestion(createManualStreamingPlaceholder(askId, callId ?? ""));
    }, [askCoachDisabled, upsertSuggestion]);

    /*
     * `coachState` est purement dérivé de 4 axes : aucune persistance, donc
     * jamais désynchronisable. Voir la docstring de `CoachState` plus haut
     * pour la machine à états complète. Priorité : recap > connexion > appel.
     */
    const coachState: CoachState = useMemo(() => {
        if (recapAvailable) return "open_post_call";
        if (wsState !== "open" || !desktopConnected) return "not_connected";
        return activeCall ? "open_in_call" : "open_idle";
    }, [activeCall, desktopConnected, recapAvailable, wsState]);

    const value = useMemo<WalterCoachContextType>(
        () => ({
            coachState,
            sessionReplaced,
            suggestions,
            callState,
            serverTime,
            isLoading,
            errorText,
            resumeSession,
            reloadInitialSuggestions,
            askCoach,
            isAskingCoach,
            askCoachDisabled,
            smartCatalog,
            isSmartCatalogPending,
            isVerbatimPending,
            refreshSmartCatalog,
            smartCatalogOptions,
            overrideSmartCatalog,
            requestVerbatims,
            fundingBasket,
            isFundingBasketPending,
            addBasketItem,
            removeBasketItem,
            setEnvelopeOverride,
            clearSuggestions: () => setSuggestions([])
        }),
        [
            coachState,
            sessionReplaced,
            suggestions,
            callState,
            serverTime,
            isLoading,
            errorText,
            resumeSession,
            reloadInitialSuggestions,
            askCoach,
            isAskingCoach,
            askCoachDisabled,
            smartCatalog,
            isSmartCatalogPending,
            isVerbatimPending,
            refreshSmartCatalog,
            smartCatalogOptions,
            overrideSmartCatalog,
            requestVerbatims,
            fundingBasket,
            isFundingBasketPending,
            addBasketItem,
            removeBasketItem,
            setEnvelopeOverride
        ]
    );

    return <WalterCoachContext.Provider value={value}>{children}</WalterCoachContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWalterCoach() {
    return useContext(WalterCoachContext);
}
